import { Building2, ExternalLink, Plus } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { Link, useOutletContext } from 'react-router'

import { agencia, type ClienteDeAgencia, useClientesDeAgencia, useCobroDeAgencia, useFichaDeAgencia, usePlanesDeAgencia, useRefrescarAgencia } from '../../api/agencia'
import { ErrorDeLaApi } from '../../api/nucleo'
import { Aviso, Boton, Campo, Esqueleto, EstadoVacio, Modal, Pastilla, Selector, Tarjeta, Titulo } from '../../componentes/ui'
import type { Contexto } from '../Modulo'
import { ApuntarPago } from './Cobros'

/**
 * «Mis clientes» (CLAUDE.md §36, fase R3): la agencia da de alta las cuentas de sus clientes con uno de
 * sus planes —el dueño recibe la invitación con la marca de la agencia—, les cambia de plan, los suspende
 * y entra en su cuenta para atenderlos. Lo que se puede y lo que no lo dice el núcleo.
 */
const mensajeDe = (e: unknown, siNo: string) => (e instanceof ErrorDeLaApi ? e.message : siNo)

const NuevoCliente = ({ espacio, cerrar, hecho }: { cerrar: () => void; espacio: number; hecho: (mensaje: string) => void }) => {
  const { data: planes } = usePlanesDeAgencia(espacio)
  const [nombre, setNombre] = useState('')
  const [dueno, setDueno] = useState('')
  const [email, setEmail] = useState('')
  const [plan, setPlan] = useState<string>('')
  const [enviando, setEnviando] = useState(false)
  const [fallo, setFallo] = useState<null | string>(null)
  const elegido = plan || String(planes?.[0]?.id ?? '')
  const enviar = async (e: FormEvent) => {
    e.preventDefault()
    setEnviando(true)
    setFallo(null)
    try {
      const r = await agencia(espacio).crearCliente({ nombre, plan: Number(elegido), propietario: { email, nombre: dueno } })
      hecho(r.mensaje)
    } catch (e) {
      setFallo(mensajeDe(e, 'No se ha podido crear la cuenta. Vuelve a intentarlo.'))
    } finally {
      setEnviando(false)
    }
  }
  if (planes && planes.length === 0) {
    return (
      <Modal ancho="max-w-lg" cerrar={cerrar} titulo="Nuevo cliente">
        <Aviso tono="info">
          Antes de dar de alta a un cliente, crea al menos un plan en <Link to={`/e/${espacio}/agencia/planes`}>Mis planes</Link>.
        </Aviso>
      </Modal>
    )
  }
  return (
    <Modal
      ancho="max-w-lg"
      cerrar={cerrar}
      pie={
        <div className="flex justify-end gap-2">
          <Boton onClick={cerrar}>Cancelar</Boton>
          <Boton cargando={enviando} form="nuevo-cliente" tipo="primario" type="submit">
            Crear e invitar
          </Boton>
        </div>
      }
      titulo="Nuevo cliente"
    >
      <form className="flex flex-col gap-4" id="nuevo-cliente" onSubmit={enviar}>
        <Campo autoFocus etiqueta="Nombre del negocio" onChange={(e) => setNombre(e.target.value)} required value={nombre} />
        <Campo etiqueta="Nombre de quien la administra" onChange={(e) => setDueno(e.target.value)} value={dueno} />
        <Campo ayuda="Le llega una invitación con tu marca para poner su contraseña." etiqueta="Su correo" onChange={(e) => setEmail(e.target.value)} required type="email" value={email} />
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">Plan</span>
          <Selector onChange={(e) => setPlan(e.target.value)} value={elegido}>
            {(planes ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
                {p.precioMensual ? ` · ${p.precioMensual} al mes` : ''}
              </option>
            ))}
          </Selector>
        </label>
        {fallo && <Aviso tono="error">{fallo}</Aviso>}
      </form>
    </Modal>
  )
}

/** Los créditos de un cliente (R4): su tope al mes de la bolsa, y créditos extra de una vez. */
const CreditosDelCliente = ({ c, espacio, cerrar, hecho }: { c: ClienteDeAgencia; cerrar: () => void; espacio: number; hecho: () => void }) => {
  const [tope, setTope] = useState(c.topePropio ? String(c.tope) : '')
  const [extra, setExtra] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [fallo, setFallo] = useState<null | string>(null)
  const guardar = async (e: FormEvent) => {
    e.preventDefault()
    setEnviando(true)
    setFallo(null)
    try {
      const nuevoTope = tope.trim() === '' ? null : Number(tope)
      const cambiaTope = nuevoTope !== (c.topePropio ? c.tope : null)
      // Sin nada que cambiar no se pregunta al núcleo: se cierra y ya.
      if (!cambiaTope && !extra.trim()) return hecho()
      await agencia(espacio).creditos(c.id, { ...(cambiaTope ? { tope: nuevoTope } : {}), ...(extra.trim() ? { extra: Number(extra) } : {}) })
      hecho()
    } catch (e) {
      setFallo(mensajeDe(e, 'No se ha podido guardar. Vuelve a intentarlo.'))
    } finally {
      setEnviando(false)
    }
  }
  return (
    <Modal
      ancho="max-w-md"
      cerrar={cerrar}
      pie={
        <div className="flex justify-end gap-2">
          <Boton onClick={cerrar}>Cancelar</Boton>
          <Boton cargando={enviando} form="creditos-del-cliente" tipo="primario" type="submit">
            Guardar
          </Boton>
        </div>
      }
      titulo={`Créditos de «${c.nombre}»`}
    >
      <form className="flex flex-col gap-4" id="creditos-del-cliente" onSubmit={guardar}>
        <p className="text-[13px] text-tenue">
          Ahora le quedan {c.creditos.toLocaleString('es')} créditos este mes. Todo lo que gasta sale de tu bolsa.
        </p>
        <Campo
          ayuda={`Cuánto puede gastar al mes. Vacío: el de su plan (${c.plan?.nombre ?? 'sin plan'}). Al cambiarlo, su mes empieza de nuevo.`}
          etiqueta="Tope al mes"
          min={0}
          onChange={(e) => setTope(e.target.value)}
          placeholder="El de su plan"
          type="number"
          value={tope}
        />
        <Campo ayuda="Créditos de una vez, que no caducan con el mes." etiqueta="Dar créditos extra" min={0} onChange={(e) => setExtra(e.target.value)} type="number" value={extra} />
        {fallo && <Aviso tono="error">{fallo}</Aviso>}
      </form>
    </Modal>
  )
}

const fecha = (iso: string) => new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })

/** Cómo va su pago a la agencia (R5), en una pastilla. Sin ningún pago apuntado no se dice nada: no vence. */
const PagoDelCliente = ({ c }: { c: ClienteDeAgencia }) => {
  if (c.pago === 'sin-pagos') return <Pastilla clave="neutra">Sin pagos</Pastilla>
  if (c.pago === 'al-dia') return <Pastilla clave="publicado">Pagado hasta el {fecha(c.pagadoHasta!)}</Pastilla>
  if (c.pago === 'por-vencer') return <Pastilla clave="construyendo">Vence el {fecha(c.pagadoHasta!)}</Pastilla>
  if (c.pago === 'en-gracia') return <Pastilla clave="construyendo">Venció el {fecha(c.pagadoHasta!)}</Pastilla>
  return <Pastilla clave="fallida">{c.suspendidaPorImpago ? 'En pausa por no pagar' : `Venció el ${fecha(c.pagadoHasta!)}`}</Pastilla>
}

const Fila = ({ c, espacio, planes, refrescar, moneda, avisar }: { avisar: (m: string) => void; c: ClienteDeAgencia; espacio: number; moneda: null | string; planes: Array<{ id: number; nombre: string }>; refrescar: () => void }) => {
  const [ocupado, setOcupado] = useState(false)
  const [conCreditos, setConCreditos] = useState(false)
  const [conPago, setConPago] = useState(false)
  const [fallo, setFallo] = useState<null | string>(null)
  const hacer = async (accion: () => Promise<unknown>) => {
    setOcupado(true)
    setFallo(null)
    try {
      await accion()
      refrescar()
    } catch (e) {
      setFallo(mensajeDe(e, 'No se ha podido guardar. Vuelve a intentarlo.'))
    } finally {
      setOcupado(false)
    }
  }
  const suspender = () => {
    if (c.estado === 'activo' && !window.confirm(`¿Suspender a «${c.nombre}»? Sus webs dejarán de verse y no podrá entrar en su panel hasta que lo reactives.`)) return
    void hacer(() => agencia(espacio).cambiarEstado(c.id, c.estado === 'activo' ? 'suspendido' : 'activo'))
  }
  return (
    <Tarjeta>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-base font-semibold">{c.nombre}</h3>
            {c.estado === 'suspendido' && !c.suspendidaPorImpago && <Pastilla clave="fallida">Suspendido</Pastilla>}
            {moneda && <PagoDelCliente c={c} />}
          </div>
          <p className="mt-0.5 text-[13px] text-tenue">
            {c.propietarios.length ? c.propietarios.map((p) => p.nombre || p.email).join(', ') : 'Sin dueño todavía'} · {c.webs === 1 ? '1 web' : `${c.webs} webs`}
            {c.webs > 0 ? ` (${c.publicadas} publicada${c.publicadas === 1 ? '' : 's'})` : ''} · le quedan {c.creditos.toLocaleString('es')} créditos (tope de {c.tope.toLocaleString('es')} al mes) · ha gastado {c.gastado30.toLocaleString('es')} en 30 días
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-44">
            <Selector aria-label={`Plan de ${c.nombre}`} disabled={ocupado} onChange={(e) => void hacer(() => agencia(espacio).cambiarPlan(c.id, Number(e.target.value)))} pequeno value={c.plan?.id ?? ''}>
              {!c.plan && <option value="">Sin plan</option>}
              {planes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </Selector>
          </div>
          {moneda && (
            <Boton onClick={() => setConPago(true)} pequeno>
              Apuntar pago
            </Boton>
          )}
          <Boton onClick={() => setConCreditos(true)} pequeno>
            Créditos
          </Boton>
          <Boton cargando={ocupado} onClick={suspender} pequeno>
            {c.estado === 'activo' ? 'Suspender' : 'Reactivar'}
          </Boton>
          <Link className="inline-flex items-center gap-1 text-[13px] font-medium text-violeta hover:underline" to={`/e/${c.id}`}>
            Entrar <ExternalLink aria-hidden className="size-3.5" />
          </Link>
        </div>
      </div>
      {fallo && (
        <div className="mt-3">
          <Aviso tono="error">{fallo}</Aviso>
        </div>
      )}
      {conPago && moneda && (
        <ApuntarPago
          c={c}
          cerrar={() => setConPago(false)}
          espacio={espacio}
          hecho={(m) => {
            setConPago(false)
            avisar(m)
            refrescar()
          }}
          moneda={moneda}
        />
      )}
      {conCreditos && (
        <CreditosDelCliente
          c={c}
          cerrar={() => setConCreditos(false)}
          espacio={espacio}
          hecho={() => {
            setConCreditos(false)
            refrescar()
          }}
        />
      )}
    </Tarjeta>
  )
}

export const Clientes = () => {
  const { espacio } = useOutletContext<Contexto>()
  const { data: ficha, isPending: cargandoFicha } = useFichaDeAgencia(espacio)
  const { data: clientes, isPending, error } = useClientesDeAgencia(espacio)
  const { data: planes } = usePlanesDeAgencia(espacio)
  const { data: cobro } = useCobroDeAgencia(espacio, Boolean(ficha?.puede.cobros))
  const refrescar = useRefrescarAgencia(espacio)
  const [nuevo, setNuevo] = useState(false)
  const [mensaje, setMensaje] = useState<null | string>(null)

  if (!cargandoFicha && !ficha?.puede.clientes) return <Aviso tono="error">Esta cuenta no es una agencia, o no llevas sus clientes.</Aviso>
  const maximo = ficha?.techo?.clientesMaximos ?? 0
  const cuantos = clientes?.length ?? 0
  const lleno = maximo > 0 && cuantos >= maximo
  return (
    <>
      <Titulo
        accion={
          <Boton disabled={lleno || !ficha?.agencia.activo} onClick={() => setNuevo(true)} tipo="primario">
            <Plus aria-hidden className="size-4" /> Nuevo cliente
          </Boton>
        }
        descripcion={maximo > 0 ? `${cuantos} de ${maximo} clientes de tu plan. Entran por ${ficha?.agencia.direccion ?? 'tu panel'}.` : `Tus clientes entran por ${ficha?.agencia.direccion ?? 'tu panel'}.`}
        rotulo={ficha?.agencia.nombre}
      >
        Mis clientes
      </Titulo>
      {ficha && !ficha.agencia.activo && <Aviso tono="aviso">Tu agencia está en pausa: no puedes dar de alta ni cambiar clientes hasta que el equipo de Wixy la reactive.</Aviso>}
      {ficha && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-caja border border-linea bg-papel px-4 py-3">
          <p className="text-sm">
            <strong className="tabular-nums">{ficha.bolsa.total.toLocaleString('es')}</strong> créditos en tu bolsa
            <span className="text-tenue"> · la gastan entre todos tus clientes, cada uno hasta su tope al mes</span>
          </p>
          <Link className="text-[13px] font-medium text-violeta hover:underline" to={`/e/${espacio}/creditos`}>
            Recargar la bolsa
          </Link>
        </div>
      )}
      {ficha && ficha.bolsa.total === 0 && <Aviso tono="aviso">Tu bolsa está vacía: tus clientes no pueden usar la IA hasta que la recargues.</Aviso>}
      {lleno && <Aviso tono="info">Has llegado a los clientes que admite tu plan. Para tener más, pide al equipo un plan de revendedor mayor.</Aviso>}
      {mensaje && <Aviso tono="ok">{mensaje}</Aviso>}
      <div className="mt-4 flex flex-col gap-3">
        {isPending ? (
          <Esqueleto />
        ) : error ? (
          <Aviso tono="error">No hemos podido leer tus clientes. Vuelve a intentarlo en un momento.</Aviso>
        ) : cuantos === 0 ? (
          <EstadoVacio
            accion={
              <Boton disabled={lleno} onClick={() => setNuevo(true)} tipo="primario">
                <Plus aria-hidden className="size-4" /> Dar de alta al primero
              </Boton>
            }
            descripcion="Crea la cuenta de tu cliente con uno de tus planes: le llega una invitación con tu marca y entra por tu panel."
            icono={<Building2 />}
            titulo="Todavía no tienes clientes"
          />
        ) : (
          clientes!.map((c) => <Fila avisar={setMensaje} c={c} espacio={espacio} key={c.id} moneda={cobro?.moneda ?? null} planes={(planes ?? []).map((p) => ({ id: p.id, nombre: p.nombre }))} refrescar={refrescar} />)
        )}
      </div>
      {nuevo && (
        <NuevoCliente
          cerrar={() => setNuevo(false)}
          espacio={espacio}
          hecho={(m) => {
            setNuevo(false)
            setMensaje(m)
            void refrescar()
          }}
        />
      )}
    </>
  )
}
